import { FC } from 'react';
import { Avatar, Button, Flex, Typography } from 'antd';
import { TbChevronRight } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { FinancialOverviewSectionHeaderProps } from '../../types';
import { FinancialOverviewSectionDragHandle } from '../FinancialOverviewSectionDragHandle';

export const FinancialOverviewSectionHeader: FC<FinancialOverviewSectionHeaderProps> = ({
    actions,
    collapseControl,
    description,
    dragControl,
    icon,
    style,
    title,
    titleId,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <Flex
            align='flex-start'
            data-financial-overview-section-header='true'
            gap='1rem'
            justify='space-between'
            onMouseEnter={() => dragControl?.onHoverChange?.(true)}
            onMouseLeave={() => dragControl?.onHoverChange?.(false)}
            style={{ minWidth: 0, ...style }}
        >
            <Flex align='flex-start' gap='0.625rem' style={{ minWidth: 0 }}>
                {dragControl ? <FinancialOverviewSectionDragHandle control={dragControl} /> : null}
                {collapseControl ? (
                    <span
                        data-financial-overview-collapse-control='true'
                        style={{
                            alignItems: 'center',
                            display: 'inline-flex',
                            flex: `0 0 ${collapseControl.visible ? '1.5rem' : 0}`,
                            marginRight: collapseControl.visible ? 0 : '-0.625rem',
                            opacity: collapseControl.visible ? 1 : 0,
                            overflow: 'hidden',
                            pointerEvents: collapseControl.visible ? 'auto' : 'none',
                            transition: `flex-basis ${token.motionDurationMid} ${token.motionEaseInOut}, margin-right ${token.motionDurationMid} ${token.motionEaseInOut}, opacity ${token.motionDurationMid} ${token.motionEaseInOut}, width ${token.motionDurationMid} ${token.motionEaseInOut}`,
                            width: collapseControl.visible ? '1.5rem' : 0,
                        }}
                    >
                        <Button
                            aria-controls={collapseControl.contentId}
                            aria-expanded={!collapseControl.collapsed}
                            aria-label={`${collapseControl.collapsed ? 'Expand' : 'Collapse'} ${collapseControl.label}`}
                            icon={
                                <span
                                    aria-hidden='true'
                                    data-financial-overview-collapse-icon='true'
                                    style={{
                                        alignItems: 'center',
                                        display: 'inline-flex',
                                        height: '100%',
                                        justifyContent: 'center',
                                        lineHeight: 0,
                                        width: '100%',
                                    }}
                                >
                                    <TbChevronRight
                                        size='1rem'
                                        style={{
                                            color: token.colorTextTertiary,
                                            display: 'block',
                                            transform: collapseControl.collapsed ? undefined : 'rotate(90deg)',
                                            transition: `transform ${token.motionDurationMid} ${token.motionEaseInOut}`,
                                        }}
                                    />
                                </span>
                            }
                            onClick={(event) => {
                                collapseControl.onChange(!collapseControl.collapsed);
                                dragControl?.onHoverChange?.(false);
                                event.currentTarget.blur();
                            }}
                            size='small'
                            style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                height: '1.5rem',
                                justifyContent: 'center',
                                lineHeight: 0,
                                width: '1.5rem',
                            }}
                            type='text'
                        />
                    </span>
                ) : null}
                {icon ? (
                    <Avatar
                        aria-hidden='true'
                        icon={
                            <span
                                style={{
                                    alignItems: 'center',
                                    display: 'inline-flex',
                                    fontSize: '1rem',
                                    height: '100%',
                                    justifyContent: 'center',
                                    lineHeight: 0,
                                    width: '100%',
                                }}
                            >
                                {icon}
                            </span>
                        }
                        shape='square'
                        size={24}
                        style={{
                            background: token.colorBgContainer,
                            border: `1px solid ${token.colorBorder}`,
                            color: token.colorTextTertiary,
                            flex: 'none',
                        }}
                    />
                ) : null}
                <div style={{ minWidth: 0 }}>
                    <div style={{ alignItems: 'center', display: 'flex', minHeight: '1.5rem' }}>
                        <Typography.Title
                            id={titleId}
                            level={4}
                            style={{ lineHeight: '1.5rem', margin: 0, minWidth: 0 }}
                        >
                            {title}
                        </Typography.Title>
                    </div>
                    {description ? (
                        <div style={{ color: token.colorTextSecondary, marginTop: '0.25rem' }}>
                            <small>{description}</small>
                        </div>
                    ) : null}
                </div>
            </Flex>
            {actions ? (
                <div style={{ alignItems: 'center', display: 'flex', flexShrink: 0, minHeight: '1.5rem' }}>
                    {actions}
                </div>
            ) : null}
        </Flex>
    );
};
